
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import LocalStorageService from '../services/storage';
import moment from 'moment';

const localStorageService = LocalStorageService();

interface Account {
    id_account: string;
    account_name: string;
    type: string;
    isactive?: boolean;
}
type AccountInput = Omit<Account, 'id_account'>

interface Balance {
    id: string;
    value: number;
    fk_accounts_id_account: string;
}
type BalanceInput = Omit<Balance, 'id'>

interface AccountsProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
    data?: any;
}

interface UseAccountsOptions {
    loadAccounts?: boolean;
    loadBalances?: boolean;
}

interface AccountsContextData {
    createAccount: (account: AccountInput) => Promise<ResponseProps>;
    updateAccount: (account: Account) => Promise<ResponseProps>;
    updateOnDelete: (id: number) => Promise<ResponseProps>;
    createBalance: (account: BalanceInput) => Promise<ResponseProps>;
    getLastBalance: (endDate: string) => Promise<ResponseProps>;
}

interface UseAccountsData extends AccountsContextData {
    accounts: Account[];
    balances: Balance[];
}

const AccountsContext = createContext<AccountsContextData>(
    {} as AccountsContextData
);

const accountsQueryKey = ['accounts'];
const accountBalancesQueryKey = ['accounts', 'balances'];
const emptyAccounts: Account[] = [];
const emptyBalances: Balance[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchAccounts() {
  const response = await api.get('accounts');
  return Array.isArray(response?.data) ? response.data : [];
}

async function fetchBalances(endDate: string) {
  const response = await api.get('/accounts_balance', {
    params: {
      endDate
    }
  });

  if (response.status === 200 && !response?.data?.error) {
    return Array.isArray(response?.data) ? response.data : [];
  }

  throw new Error(response.data && response.data.error ? response.data.error : "Erro ao buscar dados dos saldos.");
}

export function AccountsProvider({ children }: AccountsProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateAccounts() {
    queryClient.invalidateQueries({ queryKey: accountsQueryKey });
  }

  function invalidateBalances() {
    queryClient.invalidateQueries({ queryKey: accountBalancesQueryKey });
  }

  async function updateOnDelete(id: number) {
    const response = await api.post('/deleteaccounts', { id_account: id });
    if (response.status === 200) {
      invalidateAccounts();
      invalidateBalances();
      return {
        success: true,
        message: "Conta removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover conta."
    };
  }

  async function createAccount(accountInput: AccountInput) {
    const response = await api.post('/accounts', accountInput);
    if (response.status === 200) {
      invalidateAccounts();
      return {
        success: true,
        message: "Conta adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar conta."
    };

  }

  async function updateAccount(account: Account) {
    const response = await api.put('/accounts', account);
    if (response.status === 200) {
      invalidateAccounts();
      invalidateBalances();
      return {
        success: true,
        message: "Conta editada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar conta."
    };
  }

  async function createBalance(balanceInput: BalanceInput) {
    const response = await api.post('/accounts_balance', balanceInput);
    if (response.status === 200) {
      invalidateBalances();
      return {
        success: true,
        message: "Saldo adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar saldo."
    };

  }

  async function getLastBalance(endDate: string) {
    try {
      const lastBalances = await queryClient.fetchQuery({
        queryKey: [...accountBalancesQueryKey, endDate],
        queryFn: () => fetchBalances(endDate),
        staleTime: 5 * 60 * 1000
      });

      return {
        success: true,
        message: "Ok!",
        data: lastBalances
      };
    } catch (err: any) {
      return {
        success: false,
        message: err?.message || "Erro ao buscar dados dos saldos.",
        data: null
      };
    }
  }

  return (
    <AccountsContext.Provider value={{ createAccount, updateAccount, updateOnDelete, createBalance, getLastBalance }}>
      {children}
    </AccountsContext.Provider>
  );
}

export function UseAccounts(options: UseAccountsOptions = {}): UseAccountsData {
  const context = useContext(AccountsContext);
  const { loadAccounts = false, loadBalances = false } = options;
  const todayEndDate = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss");

  const accountsQuery = useQuery({
    queryKey: accountsQueryKey,
    queryFn: fetchAccounts,
    enabled: loadAccounts && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  const balancesQuery = useQuery({
    queryKey: [...accountBalancesQueryKey, todayEndDate],
    queryFn: () => fetchBalances(todayEndDate),
    enabled: loadBalances && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    accounts: accountsQuery.data || emptyAccounts,
    balances: balancesQuery.data || emptyBalances
  };
}
